import * as LDClient from 'launchdarkly-js-client-sdk'
import config from '@/utils/config.js'

/**
 * @typedef {Object} LDCompanyContext
 * @property {'company'} kind - Context type identifier
 * @property {string} key - Unique company identifier (format: 'company_{id}')
 */

/** @type {LDClient.LDClient|null} */
let ldClient = null

/** @type {boolean} */
let ldReady = false

/** @type {LDCompanyContext|null} */
let ldContext = null

/** @type {boolean} */
let debugMode = false

/**
 * LaunchDarkly application metadata, so LD attributes this app's service
 * connections separately from the other Spectora surfaces (spectora-rails,
 * spectora-web, web-next, client-portal, spectora-mobile).
 *
 * `version` is deliberately omitted: no build here exposes a commit SHA today.
 * It can be added later without disruption — LD keeps the same application and
 * simply starts recording versions against it.
 * @type {{id: string}}
 */
const LD_APPLICATION = { id: 'web-viewer' }

// ============================================================================
// Standalone exported functions
// These can be imported and used anywhere (Vuex actions, services, etc.)
// ============================================================================

/**
 * Initialize LaunchDarkly with company context
 * Should be called after company data is available (after fetchToken)
 * @param {number|string} companyId - The company ID
 * @returns {Promise<void>}
 */
export async function initLaunchDarkly(companyId) {
  const clientKey = config('LAUNCHDARKLY_CLIENT_KEY')

  if (!clientKey) {
    if (debugMode) {
      // eslint-disable-next-line no-console
      console.warn(
        '[LD] No LAUNCHDARKLY_CLIENT_KEY configured, skipping initialization'
      )
    }
    return
  }

  if (!companyId) {
    if (debugMode) {
      // eslint-disable-next-line no-console
      console.warn('[LD] No companyId provided, skipping initialization')
    }
    return
  }

  ldContext = {
    kind: 'company',
    key: `company_${companyId}`,
  }

  if (debugMode) {
    // eslint-disable-next-line no-console
    console.log('[LD] Initializing with context:', ldContext)
  }

  try {
    ldClient = LDClient.initialize(clientKey, ldContext, {
      application: LD_APPLICATION,
    })

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('LaunchDarkly initialization timeout'))
      }, 5000)

      ldClient.on('ready', () => {
        clearTimeout(timeout)
        ldReady = true
        if (debugMode) {
          // eslint-disable-next-line no-console
          console.log('[LD] SDK ready')
        }
        resolve()
      })

      ldClient.on('failed', (error) => {
        clearTimeout(timeout)
        reject(error)
      })
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[LD] Initialization failed:', error.message)
    ldReady = false
    ldClient = null
  }
}

/**
 * Check if a feature flag is enabled
 * Returns defaultValue if LD is not initialized or flag doesn't exist
 * @param {string} flagName - The feature flag name
 * @param {boolean} [defaultValue=false] - Default value if flag unavailable
 * @returns {boolean}
 */
export function isFeatureEnabled(flagName, defaultValue = false) {
  if (!ldClient || !ldReady) {
    if (debugMode) {
      // eslint-disable-next-line no-console
      console.log(
        `[LD] Not ready, returning default for ${flagName}:`,
        defaultValue
      )
    }
    return defaultValue
  }

  const value = ldClient.variation(flagName, defaultValue)
  if (debugMode) {
    // eslint-disable-next-line no-console
    console.log(`[LD] Flag ${flagName}:`, value)
  }
  return value
}

/**
 * Check if LaunchDarkly SDK is ready
 * @returns {boolean}
 */
export function isLDReady() {
  return ldReady
}

/**
 * Get the current context (for debugging)
 * @returns {LDCompanyContext|null}
 */
export function getLDContext() {
  return ldContext
}

// ============================================================================
// Vue Plugin
// Delegates to standalone functions for use in components via this.$ld
// ============================================================================

/**
 * LaunchDarkly Vue plugin for feature flag evaluation
 * Follows the Vue 2 plugin pattern used by analyticsPlugin
 */
const LaunchDarkly = {
  /**
   * Install the LaunchDarkly plugin on Vue
   * @param {import('vue').VueConstructor} Vue - Vue constructor
   * @param {Object} [opts={}] - Plugin options
   * @param {boolean} [opts.debug=false] - Enable debug logging
   */
  install(Vue, opts = {}) {
    debugMode = opts.debug || false

    Vue.prototype.$ld = {
      initWithCompany: initLaunchDarkly,
      isEnabled: isFeatureEnabled,
      isReady: isLDReady,
      getContext: getLDContext,
    }
  },
}

export default LaunchDarkly

/**
 * Reset internal state - for testing only
 * @private
 */
export const _resetForTesting = () => {
  ldClient = null
  ldReady = false
  ldContext = null
  debugMode = false
}

// Export for testing
export { ldClient, ldReady, ldContext }
